import { useState } from 'react';
import { AlertTriangle, Printer, X, Check, Eye } from 'lucide-react';

import { Card }    from '@/components/ui/Card';
import { Button }  from '@/components/ui/Button';
import { Input }   from '@/components/ui/Input';
import { Spinner } from '@/components/ui/Spinner';

import { usePermission } from '@/hooks/usePermission';
import { Permissions }   from '@/constants/permissions';
import { CROSSING_LABELS } from '@/constants/chequeCrossing';
import { chequeApi }     from '@/api/cheque.api';
import type {
  ChequePrintQueueItem, ChequePrintData, BatchPrintSkipped,
} from '@/api/cheque.api';
import { ChequePrintLayer } from '@/components/banking/ChequePrintSheet';
import { ChequePreview }    from '@/components/banking/ChequePreview';

/**
 * Prints a selection of cheques as one run (GAP-007).
 *
 * Three steps, and the middle one is the point: PREVIEW → print → confirm. The preview shows each
 * cheque exactly as it will come off the printer, because the alternative is finding out what the
 * layout does after a numbered piece of stationery has already gone through the machine — and the
 * only remedy then is a reprint, which needs a reason and puts a second cheque on that number.
 *
 * Confirming, not printing, is what records the cheques as printed. A run that jams or comes out on
 * the wrong stationery is simply not confirmed, and the cheques stay in the queue.
 */

type Step = 'setup' | 'preview';

interface Props {
  items:    ChequePrintQueueItem[];   // the selected rows, in grid order
  onCancel: () => void;
  onDone:   () => void;               // confirmed — reload the queue
}

/**
 * What each stored crossing means on paper (GAP-007).
 *
 * '' and null are both "not stated", which prints nothing — deliberately distinct from OPEN, which
 * is a decision to issue an uncrossed cheque. Showing them the same way would hide the difference
 * between nobody having said, and somebody having chosen.
 */
export const BatchPrintDialog = ({ items, onCancel, onDone }: Props) => {
  const { hasPermission } = usePermission();
  const canOverridePayee    = hasPermission(Permissions.BNK_CHEQUE_CASH_PAYEE);
  const canOverrideCrossing = hasPermission(Permissions.BNK_CHEQUE_CROSSING);

  const [step,        setStep]        = useState<Step>('setup');
  const [rubberStamp, setRubberStamp] = useState(true);
  const [payee,       setPayee]       = useState('');
  // '' means "print each cheque as registered" — deliberately distinct from 'OPEN', which is a
  // decision to print an uncrossed cheque and is recorded as one.
  const [crossing,    setCrossing]    = useState('');
  const [busy,        setBusy]        = useState(false);
  const [error,       setError]       = useState<string | null>(null);

  const [rendered, setRendered] = useState<ChequePrintData[]>([]);
  const [printedIds, setPrintedIds] = useState<string[]>([]);
  const [batchId,  setBatchId]  = useState<string | null>(null);
  const [skipped,  setSkipped]  = useState<BatchPrintSkipped[]>([]);
  const [sentToPrinter, setSentToPrinter] = useState(false);
  const [showGuides, setShowGuides] = useState(false);

  const total = items.reduce((sum, i) => sum + i.amount, 0);

  const readError = (e: unknown): string => {
    const res = (e as { response?: { data?: { errors?: Array<{ message?: string }>; message?: string } } }).response;
    return res?.data?.errors?.[0]?.message ?? res?.data?.message ?? 'That did not work.';
  };

  // The override is only ever sent by someone who holds the permission — but the server checks it
  // again, so a stale permission cache cannot get a CASH cheque printed.
  const override = canOverridePayee && payee.trim() ? payee.trim() : null;
  const crossingOverride = canOverrideCrossing && crossing ? crossing : null;

  // Renders the run and shows it. Deliberately does NOT print: the operator looks first.
  const handlePreview = async () => {
    setBusy(true); setError(null);
    try {
      const res = await chequeApi.batchPrint({
        chequeIds:         items.map(i => i.id),
        printRubberStamp:  rubberStamp,
        cashPayeeOverride: override,
        crossingOverride,
      });
      const data = res.data.data;
      if (!data || data.cheques.length === 0) throw new Error('Nothing to print.');

      setBatchId(data.batchId);
      setSkipped(data.skipped);
      setRendered(data.cheques.map(c => c.printData));
      setPrintedIds(data.cheques.map(c => c.chequeId));
      setSentToPrinter(false);
      setStep('preview');
    } catch (e) {
      setError(readError(e));
    } finally {
      setBusy(false);
    }
  };

  const handlePrint = () => {
    setSentToPrinter(true);
    // Let React paint the hidden print layer before handing the page to the printer.
    setTimeout(() => { window.print(); }, 200);
  };

  const handleConfirm = async () => {
    setBusy(true); setError(null);
    try {
      await chequeApi.batchConfirmPrint({
        chequeIds:         printedIds,
        batchId,
        printRubberStamp:  rubberStamp,
        cashPayeeOverride: override,
        crossingOverride,
      });
      onDone();
    } catch (e) {
      setError(readError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
         onClick={busy ? undefined : onCancel}>
      {/* Wider once a cheque is on screen: a preview squeezed into a dialog column is the same
          "trust me" the preview exists to replace. */}
      <div className={step === 'preview' ? 'w-full max-w-3xl' : 'w-full max-w-lg'}
           onClick={e => e.stopPropagation()}>
        <Card className="p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-slate-800 dark:text-slate-100 flex items-center gap-2">
              <Printer className="w-4 h-4 text-orange-500" />
              Print {items.length} cheque{items.length === 1 ? '' : 's'}
            </h3>
            <button onClick={onCancel} disabled={busy}
                    className="text-slate-400 hover:text-slate-600 disabled:opacity-40" aria-label="Close">
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* What is about to go through the printer, in one line. */}
          <div className="rounded border border-slate-200 dark:border-slate-700 bg-slate-50/70 dark:bg-slate-800/40 p-3 text-sm">
            <div className="flex justify-between text-slate-600 dark:text-slate-300">
              <span>Cheques</span>
              <span className="tabular-nums font-medium">{items.length}</span>
            </div>
            <div className="flex justify-between text-slate-600 dark:text-slate-300 mt-1">
              <span>Total value</span>
              <span className="tabular-nums font-medium">
                {total.toLocaleString(undefined, { minimumFractionDigits: 2 })} {items[0]?.currencyCode ?? ''}
              </span>
            </div>
            <div className="flex justify-between text-slate-500 dark:text-gray-400 mt-1">
              <span>Numbers</span>
              <span className="font-mono text-xs truncate max-w-[16rem] text-right">
                {items.map(i => i.chequeNumber).join(', ')}
              </span>
            </div>
          </div>

          {step === 'setup' && (
            <>
              <label className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-200">
                <input type="checkbox" className="mt-1" checked={rubberStamp}
                       onChange={e => setRubberStamp(e.target.checked)} />
                <span>
                  Print the rubber stamp
                  <span className="block text-xs text-slate-500 dark:text-gray-400">
                    Leave this off when the stationery is stamped by hand. Nothing prints either way
                    unless the cheque layout has stamp wording configured.
                  </span>
                </span>
              </label>

              {/* The payee is shown read-only so the operator can see what is about to be printed. */}
              <div>
                <div className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-gray-400">
                  Payee
                </div>
                <div className="mt-1 max-h-24 overflow-y-auto rounded border border-slate-200 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-800">
                  {items.map(i => (
                    <div key={i.id} className="flex justify-between gap-3 px-2 py-1 text-sm">
                      <span className="font-mono text-xs text-slate-500 dark:text-gray-400">{i.chequeNumber}</span>
                      <span className={override
                        ? 'text-slate-400 line-through truncate'
                        : 'text-slate-700 dark:text-slate-200 truncate'}>
                        {i.payeeName || '—'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* ── Crossing (GAP-007 AC2) ─────────────────────────────── */}
              <div>
                <div className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-gray-400">
                  Crossing
                </div>
                <div className="mt-1 max-h-24 overflow-y-auto rounded border border-slate-200 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-800">
                  {items.map(i => (
                    <div key={i.id} className="flex justify-between gap-3 px-2 py-1 text-sm">
                      <span className="font-mono text-xs text-slate-500 dark:text-gray-400">{i.chequeNumber}</span>
                      <span className={crossingOverride
                        ? 'text-slate-400 line-through truncate'
                        : 'text-slate-700 dark:text-slate-200 truncate'}>
                        {CROSSING_LABELS[i.crossingType ?? ''] ?? 'Not stated'}
                      </span>
                    </div>
                  ))}
                </div>

                {canOverrideCrossing ? (
                  <div className="mt-2">
                    <label className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-gray-400">
                      Change the crossing for this run
                    </label>
                    <select
                      value={crossing}
                      onChange={e => setCrossing(e.target.value)}
                      className="mt-1 w-full rounded border border-slate-300 bg-white px-2 py-1.5 text-sm
                                 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
                    >
                      <option value="">Print each cheque as registered</option>
                      <option value="ACCOUNT_PAYEE">A/C Payee Only</option>
                      <option value="NOT_NEGOTIABLE">Not Negotiable</option>
                      <option value="OPEN">Open (no crossing)</option>
                    </select>
                    {crossingOverride === 'OPEN' && (
                      <div className="mt-2 flex items-start gap-2 rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-800">
                        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                        All {items.length} cheque{items.length === 1 ? '' : 's'} will print with no
                        crossing. An open cheque can be banked by whoever presents it. This is
                        recorded against every cheque in the run.
                      </div>
                    )}
                    {crossingOverride && crossingOverride !== 'OPEN' && (
                      <p className="mt-1 text-xs text-slate-500 dark:text-gray-400">
                        Printed on this run only. The cheques stay registered as they are.
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="mt-1 text-xs text-slate-400">
                    You may not change the crossing on a print run.
                  </p>
                )}
              </div>

              {canOverridePayee && (
                <div>
                  <label className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-gray-400">
                    Override payee on every cheque in this run
                  </label>
                  <Input value={payee} onChange={e => setPayee(e.target.value)}
                         placeholder="CASH — leave blank to print the payees above" />
                  {override && (
                    <div className="mt-2 flex items-start gap-2 rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-800">
                      <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                      All {items.length} cheque{items.length === 1 ? '' : 's'} will print payable to
                      “{override}”. A cheque payable to CASH can be encashed by whoever holds it.
                      This is recorded against every cheque in the run.
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {step === 'preview' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <p className="text-sm text-slate-600 dark:text-slate-300">
                  {rendered.length === 1
                    ? 'This is the cheque as it will print.'
                    : `These are the ${rendered.length} cheques as they will print, in order.`}
                  {' '}Load the stationery, then print.
                </p>
                <label className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-gray-400">
                  <input type="checkbox" checked={showGuides}
                         onChange={e => setShowGuides(e.target.checked)} />
                  Show field positions
                </label>
              </div>

              {/* Scrolls: a run of ten cheques is ten sheets, and the operator wants to flick
                  through them rather than trust the first one. */}
              <div className="max-h-[45vh] overflow-y-auto space-y-3 rounded bg-slate-100 p-3 dark:bg-slate-900/50">
                {rendered.map((c, i) => (
                  <div key={`${c.chequeNumber}-${i}`}>
                    <div className="mb-1 flex items-center justify-between text-xs text-slate-500 dark:text-gray-400">
                      <span className="font-mono">{c.chequeNumber}</span>
                      <span>{c.bankName || c.accountNumber}</span>
                    </div>
                    <ChequePreview data={c} showGuides={showGuides} />
                  </div>
                ))}
              </div>

              {sentToPrinter && (
                <div className="rounded border border-sky-200 bg-sky-50 p-3 text-sm text-sky-800 dark:bg-sky-900/20 dark:text-sky-300 dark:border-sky-800">
                  Sent to the printer. Check the paper before confirming — confirming is what records
                  these as printed, and printing one again afterwards needs a reason.
                </div>
              )}
            </div>
          )}

          {skipped.length > 0 && (
            <div className="rounded border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-800">
              <div className="font-medium mb-1">
                {skipped.length} cheque{skipped.length === 1 ? '' : 's'} left out of this run:
              </div>
              <ul className="space-y-0.5">
                {skipped.map(s => (
                  <li key={s.chequeId}>
                    <span className="font-mono">{s.chequeNumber}</span> — {s.reason}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {error && (
            <div className="flex items-start gap-2 rounded border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700 dark:bg-rose-900/30 dark:text-rose-400 dark:border-rose-800">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> {error}
            </div>
          )}

          <div className="flex justify-end items-center gap-2">
            {busy && <Spinner />}
            <Button variant="secondary" onClick={onCancel} disabled={busy}>
              {step === 'preview' ? 'Not printed — close' : 'Cancel'}
            </Button>
            {step === 'setup' ? (
              <Button onClick={handlePreview} disabled={busy}>
                <Eye className="w-3.5 h-3.5 mr-1" /> Preview {items.length}
              </Button>
            ) : (
              <>
                <Button variant="secondary" onClick={handlePrint} disabled={busy}>
                  <Printer className="w-3.5 h-3.5 mr-1" />
                  {sentToPrinter ? 'Print again' : `Print ${rendered.length}`}
                </Button>
                {/* Confirm stays disabled until something has actually been sent to a printer:
                    the whole point of this step is that looking is not printing. */}
                <Button onClick={handleConfirm} disabled={busy || !sentToPrinter}>
                  <Check className="w-3.5 h-3.5 mr-1" /> Confirm {rendered.length} printed
                </Button>
              </>
            )}
          </div>
        </Card>
      </div>

      {/* Hidden on screen; window.print() puts this — and only this — on the paper. */}
      {rendered.length > 0 && <ChequePrintLayer cheques={rendered} />}
    </div>
  );
};
